/**
 * Shared contract for the embeddable chat widget: the `postMessage` protocol and
 * runtime detection used by BOTH the standalone loader (`src/embed/embed.ts`, built
 * to `/widget/embed.v1.js`) and the embedded public GPT page (the main app).
 *
 * Keep this module framework-free (no Vue imports) and side-effect-free so the tiny
 * loader bundle can import the protocol constants without pulling the app in.
 */

/** `message.source` on frames the embedded page posts UP to its host window. */
export const WIDGET_MESSAGE_SOURCE = 'evergpt-widget' as const
/** `message.source` on frames the host window posts DOWN into the embedded page. */
export const WIDGET_HOST_MESSAGE_SOURCE = 'evergpt-widget-host' as const

/** Query flag the loader appends to the iframe URL to switch the page into embed mode. */
export const EMBED_QUERY_FLAG = 'embed'

/** Messages the embedded page posts to its host window. */
export type WidgetToHostMessage =
  | { source: typeof WIDGET_MESSAGE_SOURCE; type: 'ready' }
  | { source: typeof WIDGET_MESSAGE_SOURCE; type: 'request-token' }
  | { source: typeof WIDGET_MESSAGE_SOURCE; type: 'close' }

/** Messages the host window posts back into the embedded page. */
export interface HostToWidgetMessage {
  source: typeof WIDGET_HOST_MESSAGE_SOURCE
  type: 'set-token'
  accessToken: string
}

/**
 * True when the current document is running as the embedded widget (the loader opens
 * the page with `?embed=1`). Query-based rather than `window.top` sniffing so the app
 * can be opened directly with the flag for testing, not only inside a real iframe.
 */
export function isEmbedRuntime(
  search: string = window.location.search,
): boolean {
  try {
    return new URLSearchParams(search).get(EMBED_QUERY_FLAG) === '1'
  } catch {
    return false
  }
}

/**
 * True when this document is running inside ANY frame, flag or not.
 *
 * Deliberately separate from {@link isEmbedRuntime}: the widget's silent-SSO and
 * host-token bridge must stay gated on the explicit `?embed=1` flag, so a page framed
 * by an arbitrary host can't opt itself into the postMessage token handoff. This
 * answers a narrower question — "would navigating break out of someone else's page?"
 * — which is true for the flagless `<iframe src=".../public/gpt/:token">` snippet
 * `publicGptEmbedSnippet` hands out, and for any hand-rolled frame around the page.
 */
export function isFramed(): boolean {
  try {
    return window.top !== window.self
  } catch {
    // A parent that makes the comparison throw is by definition a foreign frame.
    return true
  }
}

/**
 * Host origins allowed to hand a token to (or receive messages from) the embedded
 * page. Internal-only: the app's own origin (same-origin embedding / testing), any
 * `*.foundever.com` subdomain over HTTPS, and — in dev builds only — localhost.
 */
export function isAllowedEmbedHostOrigin(
  origin: string,
  selfOrigin: string = window.location.origin,
): boolean {
  if (!origin || origin === 'null') return false
  if (origin === selfOrigin) return true
  try {
    const { hostname, protocol } = new URL(origin)
    // Localhost is trusted only in dev builds; the production bundle must never accept
    // a token from a localhost parent. (Prod CSP `frame-ancestors` already blocks
    // localhost framing, so this is defence in depth, not the primary control.)
    if (
      import.meta.env.DEV &&
      (hostname === 'localhost' || hostname === '127.0.0.1')
    ) {
      return true
    }
    return (
      protocol === 'https:' &&
      (hostname === 'foundever.com' || hostname.endsWith('.foundever.com'))
    )
  } catch {
    return false
  }
}
